import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useUiStore } from "@/stores/ui-store";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useAdminQuery } from "@/hooks/useAdminApi";
import { getRoutesByTab, ROUTES_META } from "@/lib/routes-meta";
import { cn } from "@/lib/utils";

function resolveLabel(t: (k: string) => string, route: (typeof ROUTES_META)[number]) {
  const translated = t(route.labelKey);
  return translated === route.labelKey ? route.label : translated;
}

interface SidebarNavProps {
  collapsed: boolean;
}

export function SidebarNav({ collapsed }: SidebarNavProps) {
  const { t } = useTranslation("admin");
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = useUiStore((s) => s.activeTab);
  const { sections, isSuperadmin } = useAdminAccess();

  const enabledKeys = new Set(sections.filter((s) => s.enabled).map((s) => s.key));

  const isRouteVisible = (route: (typeof ROUTES_META)[number]) => {
    if (isSuperadmin) return true;
    if (!route.sectionKey) return true;
    return enabledKeys.has(route.sectionKey);
  };

  const routes = getRoutesByTab(activeTab).filter(isRouteVisible);

  // Pending approval badge count
  const { data: pendingCount } = useAdminQuery<{ count: number }>({
    endpointKey: "workflowUsers.pendingCount",
    queryOpts: { refetchInterval: 30_000 },
  });
  const badgeCount = pendingCount?.count ?? 0;

  return (
    <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
      {routes.map((route) => {
        const Icon = route.icon;
        const isActive =
          location.pathname === route.path ||
          (route.path !== "/dashboard" && location.pathname.startsWith(route.path));

        return (
          <button
            key={route.path}
            onClick={() => navigate(route.path)}
            title={collapsed ? resolveLabel(t, route) : undefined}
            className={cn(
              "flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-sidebar-accent text-sidebar-accent-foreground border-l-2 border-[hsl(var(--sidebar-active-accent))]"
                : "text-[hsl(var(--sidebar-muted))] hover:bg-sidebar-accent/50 hover:text-sidebar-foreground border-l-2 border-transparent",
              collapsed && "justify-center px-2 border-l-0",
              route.subtle && !isActive && "opacity-60",
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4 shrink-0 transition-colors",
                isActive ? "text-[hsl(var(--sidebar-active-accent))]" : "text-[hsl(var(--sidebar-muted))]/60",
              )}
            />
            {!collapsed && <span className="truncate">{resolveLabel(t, route)}</span>}
            {!collapsed && route.path === "/access-requests" && badgeCount > 0 && (
              <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                {badgeCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
